import { useQueryClient } from '@tanstack/react-query';
import { AashtowareApiModel } from '../../../../types/models/aashtoware.model';
import { CreateAashtowareApiRequest } from '../../../../types/requests/aashtoware/create-aashtoware.request';
import { DeleteAashtowareApiRequest } from '../../../../types/requests/aashtoware/delete-aashtoware.request';
import { GetAashtowareApiRequest } from '../../../../types/requests/aashtoware/get-aashtoware.request';
import { SearchAashtowareApiRequest } from '../../../../types/requests/aashtoware/search-aashtoware.request';
import { UpdateAashtowareApiRequest } from '../../../../types/requests/aashtoware/update-aashtoware.request';
import {
  createAashtoware,
  deleteAashtoware,
  getAashtoware,
  searchAashtoware,
  updateAashtoware,
} from '../../api/aashtoware.api';
import { useAxiosInfiniteQuery, useAxiosMutation, useAxiosQuery } from './use-axios.hook';

const baseQueryKey = 'aashtoware';
const searchQueryKey = 'search';

export const useCreateAashtoware = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: CreateAashtowareApiRequest) => (await createAashtoware(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, { ...result, aashtoware: data }] as const;
};

export const useUpdateAashtoware = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: UpdateAashtowareApiRequest) => (await updateAashtoware(params)).data,
    onSuccess: (aashtoware) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === baseQueryKey &&
          (queryKey[1] === searchQueryKey || (queryKey[1] as GetAashtowareApiRequest)?.aashtowareId === aashtoware.id),
      }),
  });
  return [request, { ...result, aashtoware: data }] as const;
};

export const useGetAashtoware = <T extends AashtowareApiModel>(params: GetAashtowareApiRequest) => {
  const { data, ...result } = useAxiosQuery<T>({
    queryKey: [baseQueryKey, params],
    queryFn: async () => (await getAashtoware<T>(params)).data,
    enabled: Boolean(params.aashtowareId && !Number.isNaN(params.aashtowareId)),
  });
  return { ...result, aashtoware: data } as const;
};

export const useSearchAashtoware = (params: SearchAashtowareApiRequest) => {
  const { data, ...result } = useAxiosInfiniteQuery({
    queryKey: [baseQueryKey, searchQueryKey, params],
    queryFn: async ({ pageParam }) => (await searchAashtoware({ cursor: pageParam, ...params })).data,
    initialPageParam: params.cursor,
    getNextPageParam: (lastResponse) => lastResponse.nextCursor,
  });
  return { ...result, response: data } as const;
};

export const useDeleteAashtoware = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: DeleteAashtowareApiRequest) => (await deleteAashtoware(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, result] as const;
};
